import axios from "axios";

/*
 * Attaches the admin's token to every API call, and reacts when it expires.
 *
 * WHY IT PATCHES axios.create RATHER THAN EDITING EVERY CALLER. Twenty-two
 * components each build their own instance with
 * `axios.create({ baseURL: process.env.REACT_APP_API_URL })`, and interceptors
 * registered on the default axios object do not reach them - axios.create
 * copies the defaults once, at creation, and an instance's interceptors are
 * its own.
 *
 * So the choice was twenty-two edits or one. Twenty-two edits across forms
 * nobody has a test for, where missing a single one means an admin function
 * that silently stops working, against one wrapper that cannot miss any -
 * including files added later by someone who has never read this.
 *
 * It is a monkey-patch and that is a real cost: an instance created before
 * this module is imported would escape it. index.js imports it first, before
 * App, and every one of those instances is built during render, which is
 * later. The honest long-term fix is a shared api client the components
 * import, which is a refactor of its own and not one to do in the same change
 * as adding authentication.
 */

const STORAGE_KEY = "user";

/** The stored token, or null. Never throws - localStorage can be unavailable. */
export function currentToken() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const user = JSON.parse(raw);
        return user && typeof user.token === "string" && user.token ? user.token : null;
    } catch (err) {
        return null;
    }
}

/*
 * A 401 means the token has expired or been revoked, and the stored session
 * is now a lie: the UI would keep showing admin screens whose every save
 * fails. Clearing it and sending them to the login page is the only honest
 * response.
 *
 * Guarded so a page issuing several requests at once does not redirect
 * several times, and scoped to admin paths - a 401 from somewhere else should
 * not throw an ordinary visitor out of whatever they were reading.
 */
let handlingExpiry = false;

function onUnauthorised() {
    if (handlingExpiry) return;
    handlingExpiry = true;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
        /* nothing to clear */
    }
    if (typeof window !== "undefined" && window.location) {
        const onAdmin = /^\/(dashboard|login|new-|edit-|manage)/.test(window.location.pathname);
        if (onAdmin) window.location.assign("/login");
    }
}

/*
 * The token goes to OUR API and nowhere else.
 *
 * `attach(axios)` below puts this interceptor on the default axios object, and
 * several places call that directly with an absolute URL - most importantly
 * the four photo uploads, which PUT straight to a presigned S3 URL. S3 rejects
 * a presigned request that also carries an Authorization header ("Only one
 * auth mechanism allowed", HTTP 400), so from 14 Oct 2026, when this file
 * shipped, every photo upload failed - while the spinner never stopped,
 * because the failure is only logged to the console.
 *
 * It was also sending the admin's token to every other host those default
 * calls reach (the contact-form gateway, the dead reviews proxy). Scoping it
 * to the API's own origin fixes both.
 */
function apiOrigin() {
    try {
        return new URL(process.env.REACT_APP_API_URL).origin;
    } catch (err) {
        return null;
    }
}

function targetOrigin(config) {
    try {
        const url = config.url || "";
        const absolute = /^[a-z][a-z\d+\-.]*:\/\//i.test(url);
        return new URL(absolute ? url : (config.baseURL || "") + url).origin;
    } catch (err) {
        return null;
    }
}

function isApiRequest(config) {
    const api = apiOrigin();
    return api !== null && targetOrigin(config) === api;
}

function attach(instance) {
    instance.interceptors.request.use(function (config) {
        const token = currentToken();
        if (token && isApiRequest(config)) {
            config.headers = config.headers || {};
            // Only if the caller has not set one itself - a component with a
            // reason to send something else should win over a blanket rule.
            if (!config.headers.Authorization) {
                config.headers.Authorization = "Bearer " + token;
            }
        }
        return config;
    });

    instance.interceptors.response.use(
        function (response) {
            return response;
        },
        function (error) {
            if (error && error.response && error.response.status === 401) {
                onUnauthorised();
            }
            return Promise.reject(error);
        }
    );

    return instance;
}

export default function installApiAuth() {
    attach(axios);

    const create = axios.create.bind(axios);
    axios.create = function (config) {
        return attach(create(config));
    };
}
